import type {
  LoginRequest,
  RequestPasswordResetRequest,
  ResetPasswordRequest,
  AdminLoginResponse,
} from '../types/auth.type';
import type { IHttpResponse } from '@/core/api/HttpClient';
import httpClient from '@/core/api/createAxiosHttpClient';

/**
 * Factory function to create Auth API service
 * @param httpClient - HTTP client instance
 */
export const authApi = {
  /**
   * Inicia sesión con las credenciales proporcionadas.
   * @param data - Objeto que contiene el correo electrónico y la contraseña del usuario.
   * @returns Una promesa que resuelve con la respuesta de la API, incluyendo los datos del usuario autenticado.
   */
  login: async (data: LoginRequest): Promise<IHttpResponse<AdminLoginResponse>> => {
    return await httpClient.postPublic<LoginRequest, AdminLoginResponse>(
      '/admin/auth/sign-in',
      data,
    );
  },

  /**
   * Solicita un restablecimiento de contraseña
   * @param data - Correo electrónico del usuario
   */
  requestPasswordReset: async (data: RequestPasswordResetRequest): Promise<IHttpResponse<void>> => {
    return await httpClient.postPublic<RequestPasswordResetRequest, void>(
      '/admin/auth/request-password-reset',
      data,
    );
  },

  /**
   * Valida el token de restablecimiento de contraseña
   * @param token - Token de restablecimiento
   */
  validatePasswordResetToken: async (token: string): Promise<IHttpResponse<void>> => {
    return await httpClient.post<{ token: string }, void>(
      '/admin/auth/validate-password-reset-token',
      {
        token: token,
      },
    );
  },

  /**
   * Restablece la contraseña del usuario
   * @param data - Nueva contraseña y token
   */
  resetPassword: async (data: ResetPasswordRequest): Promise<IHttpResponse<void>> => {
    return await httpClient.postPublic<ResetPasswordRequest, void>(
      '/admin/auth/reset-password',
      data,
    );
  },

  /**
   * Cierra la sesión del usuario autenticado en el backend.
   */
  signOut: async (): Promise<IHttpResponse<void>> => {
    return await httpClient.post<undefined, void>('/admin/auth/sign-out', undefined);
  },
};
